import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { default as Form } from '../../components/connectedForm';
import * as input from '../../components/connectedForm/inputComponents';
import PurchaseInvoiceTransactionQuery from '../../graphql/queries/supplier/PurchaseInvoiceTransactionQuery.graphql';
import PurchaseCreditTransactionQuery from '../../graphql/queries/supplier/PurchaseCreditTransactionQuery.graphql';
import PurchaseVatOnlyTransactionQuery from '../../graphql/queries/supplier/PurchaseVatOnlyTransactionQuery.graphql';
import PurchasePaymentTransactionQuery from '../../graphql/queries/supplier/PurchasePaymentTransactionQuery.graphql';
import SupplierOverdueBalanceQuery from '../../graphql/queries/supplier/SupplierOverdueBalanceQuery.graphql';
import PurchaseTransactionAllocationQuery from '../../graphql/queries/supplier/PurchaseTransactionAllocationQuery.graphql';
import PurchaseLedgerJournalTransactionQuery from '../../graphql/queries/purchases/PurchaseLedgerJournalTransactionQuery.graphql';
import ValueCard from '../../components/connectedForm/ValueCard';
import { PurchaseInvoice, Supplier } from '../../graphql/entities';
import PurchaseTransactionWorkspace from '../../views/supplier/PurchaseTransactionWorkspace';
import ValueBalanceBar from '../../components/ValueBalanceBar/ValueBalanceBar';
import styled from 'styled-components';
import Formatters from '../../components/grid-addons/formatters';
import getDocumentModalProps from '../../utils/getDocumentModalProps';
import PurchaseTransaction from '../../graphql/entities/supplier/PurchaseTransaction';
import WorkspaceModal from '../../components/Workspace/WorkspaceModal';
import PurchaseInvoiceWorkspace from './PurchaseInvoiceWorkspace';
import ReactTooltip from 'react-tooltip';
import { useTotalQuery } from './SupplierOverviewTab';
import CurrencyContext from '../../components/Context/CurrencyContext';
import {
  CostViewPermission,
  DocumentType,
  FilterOperation,
  IFilterInput,
  IOverdueBalance,
  IPostedTransactionsResponse,
  IPurchaseAllocationLine,
  IPurchaseCreditTransactionsQueryResult,
  IPurchaseCreditTransactionsQueryVariables,
  IPurchaseInvoiceTransactionsQueryResult,
  IPurchaseInvoiceTransactionsQueryVariables,
  IPurchaseLedgerJournalTransactionsQueryResult,
  IPurchaseLedgerJournalTransactionsQueryVariables,
  IPurchaseOverdueBalanceQueryResult,
  IPurchaseOverdueBalanceQueryVariables,
  IPurchasePaymentTransactionsQueryResult,
  IPurchasePaymentTransactionsQueryVariables,
  IPurchaseTransaction,
  IPurchaseTransactionAllocationsQueryResult,
  IPurchaseTransactionAllocationsQueryVariables,
  IPurchaseVatOnlyTransactionsQueryResult,
  IPurchaseVatOnlyTransactionsQueryVariables,
  ISupplier,
  Maybe,
} from '../../graphql/schema/p4types';
import { useHistory } from 'react-router';
import { QueryTuple, useLazyQuery, useQuery } from '@apollo/client';
import { DocumentNode } from 'graphql';
import {
  EnquiryAllocationTransactions,
  EnquiryNominalTransactions,
  EnquiryVatTransactions,
  getBalanceColors,
  getTransactionEnquiryAccountStatusIcon,
  getTransactionEnquiryCurrencyIcon,
  getTransactionEnquiryGridHeight,
  renderTransactionEnquiryHeader,
  TransactionEnquiryHeaderBlock,
} from '../salesLedger/SalesTransactionEnquiryForm';
import QueryDropdown from '../../components/connectedForm/QueryDropdown';
import { useSelector } from 'react-redux';
import { GuidedTour } from '../../components/Teaching/components/GuidedTour/GuidedTour';
import { TabButton } from '../../components/connectedForm/TabButton/TabButton';
import { ModalFormatter } from '../../components/grid-addons/formatters/ModalFormatter';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';

const LazyPurchaseAllocationModal = React.lazy(() => import('../../components/modals/PurchaseAllocationModal'));

const SubHeader = styled.div`
  display: inline-block;
  font-size: 12px;
  font-weight: 400;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.54);
  padding-right: 30px;
`;

const ALLOCATION_COLUMN_META = [
  {
    name: 'Allocation Date',
    key: 'purchaseAllocation.documentDate',
    formatter: Formatters.DateFormatter,
    resizable: true,
  },
  {
    name: 'Document No',
    key: 'purchaseAllocation.documentNumber',
    resizable: true,
    formatter: ModalFormatter,
    options: {
      modalContent: LazyPurchaseAllocationModal,
      entityId: 'purchaseAllocation.id',
      modalProps: getDocumentModalProps,
    },
  },
  {
    name: 'Total Allocations In Batch',
    key: 'purchaseAllocation.debitTotal',
    resizable: true,
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
    cellClass: 'rightAlign',
  },
  {
    name: 'Allocated Amount',
    key: 'allocatedTotal',
    resizable: true,
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
    cellClass: 'rightAlign',
  },
];

const useNominalVatLazyQuery = <TData, TVariables>(
  query: DocumentNode,
  setNominalVat: (data: IPostedTransactionsResponse) => void
): QueryTuple<TData, TVariables> => {
  const [transactionQuery, queryTuple] = useLazyQuery<TData, TVariables>(query, {
    fetchPolicy: 'no-cache',
  });
  const { data } = queryTuple;

  useEffect(() => {
    if (data) {
      setNominalVat((data as any).data as IPostedTransactionsResponse);
    }
  }, [data, setNominalVat]);

  return [transactionQuery, queryTuple];
};

const useNominalVatState = (
  transaction: Maybe<IPurchaseTransaction>
): [IPostedTransactionsResponse, Dispatch<SetStateAction<IPostedTransactionsResponse>>] => {
  const [nominalVat, setNominalVat] = useState<IPostedTransactionsResponse>({
    nominalTransactions: [],
    vatTransactions: [],
  });

  const [purchaseInvoiceTransactionQuery] = useNominalVatLazyQuery<
    IPurchaseInvoiceTransactionsQueryResult,
    IPurchaseInvoiceTransactionsQueryVariables
  >(PurchaseInvoiceTransactionQuery, setNominalVat);
  const [purchaseCreditTransactionQuery] = useNominalVatLazyQuery<
    IPurchaseCreditTransactionsQueryResult,
    IPurchaseCreditTransactionsQueryVariables
  >(PurchaseCreditTransactionQuery, setNominalVat);
  const [purchasePaymentTransactionQuery] = useNominalVatLazyQuery<
    IPurchasePaymentTransactionsQueryResult,
    IPurchasePaymentTransactionsQueryVariables
  >(PurchasePaymentTransactionQuery, setNominalVat);
  const [purchaseVatOnlyTransactionQuery] = useNominalVatLazyQuery<
    IPurchaseVatOnlyTransactionsQueryResult,
    IPurchaseVatOnlyTransactionsQueryVariables
  >(PurchaseVatOnlyTransactionQuery, setNominalVat);
  const [purchaseLedgerJournalTransactionQuery] = useNominalVatLazyQuery<
    IPurchaseLedgerJournalTransactionsQueryResult,
    IPurchaseLedgerJournalTransactionsQueryVariables
  >(PurchaseLedgerJournalTransactionQuery, setNominalVat);

  useEffect(() => {
    const variables = { variables: { id: transaction?.sourceId } };
    switch (transaction?.documentType) {
      case DocumentType.PurchaseVatInvoice:
      case DocumentType.PurchaseInvoice:
        purchaseInvoiceTransactionQuery(variables);
        break;
      case DocumentType.PurchaseCredit:
        purchaseCreditTransactionQuery(variables);
        break;
      case DocumentType.PurchasePayment:
        purchasePaymentTransactionQuery(variables);
        break;
      case DocumentType.PurchaseVatCredit:
        purchaseVatOnlyTransactionQuery(variables);
        break;
      case DocumentType.PurchaseLedgerJournal:
        purchaseLedgerJournalTransactionQuery(variables);
        break;
      default:
        setNominalVat({
          nominalTransactions: [],
          vatTransactions: [],
        });
        break;
    }
  }, [
    purchaseCreditTransactionQuery,
    purchaseInvoiceTransactionQuery,
    purchasePaymentTransactionQuery,
    purchaseVatOnlyTransactionQuery,
    purchaseLedgerJournalTransactionQuery,
    transaction,
  ]);

  return [nominalVat, setNominalVat];
};

const availableColumnsFilter = (column: IEditableDataGridColumnDef) => {
  return (
    column?.fieldName &&
    column.fieldName !== 'paymentMethod.description' &&
    column.fieldName !== 'paymentTerm.description' &&
    !column.fieldName.includes('balance.') &&
    !column.fieldName.includes('supplier.') &&
    !column.fieldName.includes('deliverySupplier.')
  );
};

const availableFiltersFilter = (column: IEditableDataGridColumnDef) => {
  return (
    column?.fieldName &&
    column.fieldName !== 'paymentMethod.description' &&
    column.fieldName !== 'paymentTerm.description' &&
    !column.fieldName.includes('balance.') &&
    !column.fieldName.includes('supplier.') &&
    !column.fieldName.includes('deliverySupplier.')
  );
};

const PurchaseTransactionEnquiryForm = ({
  supplier: propsSupplier,
  embedded,
  tourVisible,
}: {
  supplier: Maybe<ISupplier>;
  embedded: boolean;
  tourVisible?: boolean;
}) => {
  const [supplier, setSupplier] = useState<Maybe<ISupplier>>(null);
  const [outstanding, setOutstanding] = useState(false);
  const [balance, setBalance] = useState<Maybe<IOverdueBalance>>(null);
  const [purchaseTransaction, setPurchaseTransaction] = useState<Maybe<IPurchaseTransaction>>(null);
  const [allocation, setAllocation] = useState<Array<IPurchaseAllocationLine>>([]);
  const [purchaseTransactionsState, setPurchaseTransactionsState] = useState<{
    modalOpen: boolean;
    filters?: Array<IFilterInput>;
  }>({ modalOpen: false });
  const [purchaseInvoicesState, setPurchaseInvoicesState] = useState<{
    modalOpen: boolean;
    filters?: Array<IFilterInput>;
  }>({ modalOpen: false });
  const [nominalVat] = useNominalVatState(purchaseTransaction);
  const [showTabs, setShowTabs] = useState(false);
  const [stepOverride, setStepOverride] = useState<number | null>(null);

  const costViewPermission = useSelector((store: any) => store.app?.fullUserDetails?.companyUser?.costViewPermission);

  const history = useHistory();

  useEffect(() => {
    setSupplier(propsSupplier);
  }, [propsSupplier]);

  const filter = useMemo<Array<IFilterInput>>(() => {
    const newFilters = [
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'supplier.id',
          value: supplier && supplier.id,
        },
      },
    ];
    if (outstanding) {
      newFilters.push({
        filterOperation: FilterOperation.NotEquals,
        filterParameters: {
          field: 'outstandingAmount',
          value: 0,
        },
      });
    }
    return newFilters;
  }, [outstanding, supplier]);

  const setSupplierAndReset = (supplier: Maybe<ISupplier>) => {
    setSupplier(supplier);
    setPurchaseTransaction(null);
    setAllocation([]);
  };

  const { data: balanceData } = useQuery<IPurchaseOverdueBalanceQueryResult, IPurchaseOverdueBalanceQueryVariables>(
    SupplierOverdueBalanceQuery,
    {
      skip: !supplier,
      variables: { id: supplier?.id },
    }
  );

  useEffect(() => {
    setBalance(balanceData?.data as IOverdueBalance);
  }, [balanceData]);

  const { data: purchaseTransactionAllocationData } = useQuery<
    IPurchaseTransactionAllocationsQueryResult,
    IPurchaseTransactionAllocationsQueryVariables
  >(PurchaseTransactionAllocationQuery, {
    variables: { id: purchaseTransaction?.id },
    fetchPolicy: 'no-cache',
    skip: !purchaseTransaction?.id,
  });

  useEffect(() => {
    let allocations: Array<IPurchaseAllocationLine> = [];
    if (purchaseTransactionAllocationData?.data?.purchaseAllocationLines) {
      allocations = purchaseTransactionAllocationData.data.purchaseAllocationLines.map((line) => {
        if (!line!.purchaseAllocation?.documentNumber) {
          return {
            ...line,
            purchaseAllocation: { ...line?.purchaseAllocation, documentNumber: 'Payment' },
          } as IPurchaseAllocationLine;
        }
        return line as IPurchaseAllocationLine;
      });
    }
    setAllocation(allocations);
  }, [purchaseTransactionAllocationData]);

  const emptyRows = (type: 'Nominal' | 'VAT' | 'Allocation') => {
    return (
      <div className="no-results">
        <p style={{ textAlign: 'center' }}>{`No linked ${type} transactions`}</p>
      </div>
    );
  };

  const renderTransactionDetails = (base: boolean) => {
    if (!purchaseTransaction) return null;
    const { ValueField, DateField } = input;
    const vatable =
      purchaseTransaction.documentType === DocumentType.PurchaseInvoice ||
      purchaseTransaction.documentType === DocumentType.PurchaseCredit;
    const payment =
      purchaseTransaction.documentType === DocumentType.PurchasePayment ||
      purchaseTransaction.documentType === DocumentType.PurchaseRefund;
    let {
      amount,
      outstandingAmount,
      goodsAmount,
      vatAmount,
      goodsAmountLessSettlementDiscount,
      vatAmountLessSettlementDiscount,
      discountTaken,
    } = purchaseTransaction;
    if (base) {
      amount = purchaseTransaction.baseAmount;
      outstandingAmount = purchaseTransaction.baseOutstandingAmount;
      goodsAmount = purchaseTransaction.baseGoodsAmount;
      vatAmount = purchaseTransaction.baseVatAmount;
      goodsAmountLessSettlementDiscount = purchaseTransaction.baseGoodsAmountLessSettlementDiscount;
      vatAmountLessSettlementDiscount = purchaseTransaction.baseVatAmountLessSettlementDiscount;
      discountTaken = purchaseTransaction.baseDiscountTaken;
    }
    const nettAmount = amount - discountTaken;
    return (
      <React.Fragment>
        {renderTransactionEnquiryHeader(purchaseTransaction, DocumentType.PurchaseInvoice)}
        <Form.Row>
          <Form.Fieldset cols={1} collapsed={true}>
            <SubHeader>{purchaseTransaction.note}</SubHeader>
          </Form.Fieldset>
        </Form.Row>
        <Form.Row>
          <Form.Fieldset cols={1 / 5}>
            <ValueField
              label="Total Amount"
              view={true}
              value={amount}
              decimals={2}
              data-testid={'transaction-details-total-amount'}
            />
          </Form.Fieldset>
          <Form.Fieldset cols={1 / 5}>
            <ValueField
              label="Outstanding"
              view={true}
              value={outstandingAmount}
              decimals={2}
              data-testid={'transaction-details-outstanding-amount'}
            />
          </Form.Fieldset>
          <Form.Fieldset cols={1 / 5}>
            <DateField
              label="Document Date"
              view={true}
              value={purchaseTransaction.documentDate}
              readOnly={true}
              data-testid={'transaction-details-document-date'}
              onChange={() => {}}
              onBlur={() => {}}
            />
          </Form.Fieldset>
          <Form.Fieldset cols={2 / 5}>
            <ValueField
              label="Document Reference"
              view={true}
              value={purchaseTransaction.documentReference || ''}
              type="text"
              data-testid={'transaction-details-document-reference'}
            />
          </Form.Fieldset>
        </Form.Row>
        {vatable ? (
          <>
            <Form.Row>
              <Form.Fieldset cols={1 / 5}>
                <ValueField
                  label="Goods Amount"
                  view={true}
                  value={goodsAmount}
                  decimals={2}
                  data-testid={'transaction-details-goods-amount'}
                />
              </Form.Fieldset>
              <Form.Fieldset cols={1 / 5}>
                <ValueField
                  label="VAT Amount"
                  view={true}
                  value={vatAmount}
                  decimals={2}
                  data-testid={'transaction-details-vat-amount'}
                />
              </Form.Fieldset>
              <Form.Fieldset cols={1 / 5}>
                <ValueField
                  label="Posting Period"
                  view={true}
                  value={purchaseTransaction.financialPeriod?.name || ''}
                  type="text"
                  data-testid={'transaction-details-posting-period'}
                />
              </Form.Fieldset>
              {balance?.foreignCurrency ? (
                <Form.Fieldset cols={1 / 5}>
                  <ValueField
                    label="Exchange Rate"
                    view={true}
                    value={purchaseTransaction.exchangeRate}
                    decimals={5}
                    data-testid={'transaction-details-exchange-rate'}
                  />
                </Form.Fieldset>
              ) : null}
            </Form.Row>
            <Form.Row>
              <Form.Fieldset cols={1 / 5}>
                <DateField
                  label="Payment Due Date"
                  view={true}
                  value={purchaseTransaction.paymentDueDate}
                  readOnly={true}
                  data-testid={'transaction-details-payment-due-date'}
                  onChange={() => {}}
                  onBlur={() => {}}
                />
              </Form.Fieldset>
              <Form.Fieldset cols={1 / 5}>
                <DateField
                  label="Settlement Date"
                  view={true}
                  value={purchaseTransaction.settlementDueDate}
                  readOnly={true}
                  data-testid={'transaction-details-settlement-due-date'}
                  onChange={() => {}}
                  onBlur={() => {}}
                />
              </Form.Fieldset>
              <Form.Fieldset cols={1 / 5}>
                <ValueField
                  label="Goods Less Settlement"
                  view={true}
                  value={goodsAmountLessSettlementDiscount}
                  decimals={2}
                  data-testid={'transaction-details-goods-less-settlement'}
                />
              </Form.Fieldset>
              <Form.Fieldset cols={1 / 5}>
                <ValueField
                  label="VAT Less Settlement"
                  view={true}
                  value={vatAmountLessSettlementDiscount}
                  decimals={2}
                  data-testid={'transaction-details-vat-less-settlement'}
                />
              </Form.Fieldset>
              <Form.Fieldset cols={1 / 5}>
                <ValueField
                  label="VAT Credit Expected"
                  view={true}
                  value={purchaseTransaction.vatCreditExpected ? 'Yes' : 'No'}
                  currency={false}
                  data-testid={'transaction-details-vat-credit-expected'}
                />
              </Form.Fieldset>
            </Form.Row>
          </>
        ) : null}
        {payment ? (
          <React.Fragment>
            <Form.Row>
              <Form.Fieldset cols={1 / 5}>
                <ValueField
                  label="Payment/Refund Amount"
                  view={true}
                  value={nettAmount}
                  decimals={2}
                  data-testid={'transaction-details-payment-refund-amount'}
                />
              </Form.Fieldset>
              <Form.Fieldset cols={1 / 5}>
                <ValueField
                  label="Discount"
                  view={true}
                  value={discountTaken}
                  decimals={2}
                  data-testid={'transaction-details-discount'}
                />
              </Form.Fieldset>
              <Form.Fieldset cols={1 / 5}>
                <ValueField
                  label="Posting Period"
                  view={true}
                  value={purchaseTransaction.financialPeriod?.name || ''}
                  type="text"
                  data-testid={'transaction-details-posting-period-receipt'}
                />
              </Form.Fieldset>
            </Form.Row>
          </React.Fragment>
        ) : null}
        {!vatable && !payment ? (
          <Form.Row>
            <Form.Fieldset cols={1 / 5}>
              <ValueField
                label="Posting Period"
                view={true}
                value={purchaseTransaction.financialPeriod?.name || ''}
                type="text"
                data-testid={'transaction-details-posting-period-other'}
              />
            </Form.Fieldset>
            {balance?.foreignCurrency ? (
              <Form.Fieldset cols={1 / 5}>
                <ValueField
                  label="Exchange Rate"
                  view={true}
                  value={purchaseTransaction.exchangeRate}
                  decimals={5}
                  data-testid={'transaction-details-exchange-rate-other'}
                />
              </Form.Fieldset>
            ) : null}
          </Form.Row>
        ) : null}
      </React.Fragment>
    );
  };

  const supplierFilter: IFilterInput = {
    filterOperation: FilterOperation.Equals,
    filterParameters: {
      field: 'supplier.id',
      value: supplier?.id,
    },
  };
  const disputedInvoicesFilters: Array<IFilterInput> = [
    supplierFilter,
    { filterOperation: FilterOperation.Equals, filterParameters: { field: 'held', value: 'true' } },
    { filterOperation: FilterOperation.GreaterThan, filterParameters: { field: 'outstandingAmount', value: '0' } },
  ];
  const invoicesAwaitingApprovalFilters: Array<IFilterInput> = [
    supplierFilter,
    { filterOperation: FilterOperation.Equals, filterParameters: { field: 'documentStatus', value: 'DRAFT' } },
  ];

  const outstandingSum = useTotalQuery(PurchaseTransaction.aggregate, {
    filters: disputedInvoicesFilters,
    sumColumn: 'outstandingAmount',
  });
  const awaitingApprovalSum = useTotalQuery(PurchaseInvoice.aggregate, {
    filters: invoicesAwaitingApprovalFilters,
    sumColumn: 'documentAmount',
  });

  const displayPurchaseTransactionModal = (filters: Array<IFilterInput>) => {
    setPurchaseTransactionsState({ modalOpen: true, filters: filters });
  };
  const displayPurchaseInvoiceModal = (filters: Array<IFilterInput>) => {
    setPurchaseInvoicesState({ modalOpen: true, filters: filters });
  };

  const colors = balance && getBalanceColors(balance);
  const currencyIcon = getTransactionEnquiryCurrencyIcon(balance);
  const accountStatusIcon = getTransactionEnquiryAccountStatusIcon(balance);
  const foreignCurrency = balance?.foreignCurrency;
  const currency = balance?.currency;
  return (
    <CurrencyContext.Provider value={{ currency: supplier?.currency }}>
      <ReactTooltip id={'supplierOverviewTooltip'} />
      <WorkspaceModal
        component={PurchaseTransactionWorkspace}
        viewScreenName={'/purchases/PurchaseTransactionWorkspace/Modal'}
        modalState={purchaseTransactionsState}
        closeModal={() => {
          setPurchaseTransactionsState({ modalOpen: false });
        }}
      />
      <WorkspaceModal
        component={PurchaseInvoiceWorkspace}
        viewScreenName={'/purchases/PurchaseInvoicesWorkspace/Modal'}
        modalState={purchaseInvoicesState}
        closeModal={() => {
          setPurchaseInvoicesState({ modalOpen: false });
        }}
      />
      <Form.FormContainer data-testid={'purchase-transaction-enquiry-form'}>
        {costViewPermission === CostViewPermission.All && tourVisible && (
          <GuidedTour
            tourId={'purchase-transaction-enquiry-form-tour'}
            title={"We've Made Some Changes"}
            message={'The transaction details panel is now hidden until you click a row in the table'}
            activeStep={stepOverride}
            onTourStarted={() => {
              setStepOverride(0);
            }}
            steps={[
              {
                target: `//*[@data-testid="grid-container-view-/supplier/TransactionEnquiry"]`,
                primaryBtnText: 'Try it out',
                text: 'The table has been expanded to show more records. Selecting a transaction will show its details',
                primaryBtnAction: () => {
                  setShowTabs(true);
                  const rows = document.querySelectorAll(
                    '[data-testid^="datagrid-cell-"][data-testid$="-transactionNumber"]'
                  );
                  const row = Array.from(rows).find((row) => /\d/.test(row.getAttribute('data-testid') ?? ''));
                  const clickEvent = new MouseEvent('click', {
                    view: window,
                    bubbles: true,
                    cancelable: true,
                  });
                  row?.dispatchEvent(clickEvent);
                },
              },
              {
                target: `//*[@id='pt_tab_container']`,
                text: 'View the selected transaction details here',
              },
              {
                target: `//button[text()="Hide Tabs"]`,
                text: 'Hide the tab panel for more space to view transactions',
                primaryBtnText: 'Conclude tour',
              },
            ]}
          />
        )}
        {!embedded ? (
          <TransactionEnquiryHeaderBlock
            onCancel={() => history.goBack()}
            title={'Suppliers'}
            value={'Purchase Transaction Enquiry'}
          />
        ) : null}
        <Form.Form>
          <Form.Row>
            <Form.Fieldset cols={1 / 3}>
              {!embedded ? (
                <QueryDropdown
                  label="Supplier"
                  {...Supplier.dropdown}
                  onChange={setSupplierAndReset}
                  value={supplier}
                  autoFocus={true}
                  data-testid={'supplier'}
                />
              ) : null}
              <Form.Row>
                <Form.Fieldset cols={1 / 2}>
                  <input.Switch
                    label="Outstanding Only"
                    onChange={setOutstanding}
                    labelCols={6 / 12}
                    inputCols={6 / 12}
                    value={outstanding}
                  />
                </Form.Fieldset>
                {!embedded ? (
                  <>
                    <Form.Fieldset cols={1 / 4}>{currencyIcon}</Form.Fieldset>
                    <Form.Fieldset cols={1 / 4}>{accountStatusIcon}</Form.Fieldset>
                  </>
                ) : null}
              </Form.Row>
            </Form.Fieldset>
            {balance ? (
              <Form.Fieldset cols={2 / 3}>
                <Form.Row>
                  <Form.Fieldset cols={1 / 5} collapsed={true}>
                    <ValueCard
                      readOnly={true}
                      value={outstandingSum}
                      label="Invoices in dispute"
                      onClick={() => displayPurchaseTransactionModal(disputedInvoicesFilters)}
                      tooltip={'View disputed purchase transactions'}
                      tooltipFor={'supplierOverviewTooltip'}
                      currency={currency}
                      decimals={2}
                      labelPosition="top"
                      color={colors?.outstanding}
                      collapsed={true}
                      type={'currency'}
                    />
                  </Form.Fieldset>
                  <Form.Fieldset cols={1 / 5} collapsed={true}>
                    <ValueCard
                      readOnly={true}
                      value={awaitingApprovalSum}
                      label="Awaiting approval"
                      onClick={() => displayPurchaseInvoiceModal(invoicesAwaitingApprovalFilters)}
                      tooltip={'View invoices awaiting approval'}
                      tooltipFor={'supplierOverviewTooltip'}
                      currency={currency}
                      decimals={2}
                      labelPosition="top"
                      color={colors?.outstanding}
                      collapsed={true}
                      type={'currency'}
                    />
                  </Form.Fieldset>
                  <Form.Fieldset cols={1 / 5} collapsed={true}>
                    <ValueCard
                      decimals={2}
                      label="Outstanding Amount"
                      labelPosition="top"
                      value={balance.outstandingAmount}
                      color={colors?.outstanding}
                      collapsed={true}
                      type={'currency'}
                    />
                  </Form.Fieldset>
                  <Form.Fieldset cols={1 / 5} collapsed={true}>
                    <ValueCard
                      decimals={2}
                      label="Overdue Amount"
                      labelPosition="top"
                      value={balance.overdueAmount}
                      color={colors?.overdue}
                      collapsed={true}
                      type={'currency'}
                    />
                  </Form.Fieldset>
                  <Form.Fieldset cols={1 / 5} collapsed={true}>
                    <ValueCard
                      decimals={2}
                      label="Credit Limit"
                      labelPosition="top"
                      value={balance.creditLimit}
                      collapsed={true}
                      type={'currency'}
                    />
                  </Form.Fieldset>
                </Form.Row>
                <Form.Row>
                  <Form.Fieldset cols={4 / 5}>
                    {balance.outstandingAmount !== 0 ? (
                      <ValueBalanceBar
                        backgroundColor={colors?.barColor}
                        color={colors?.barProgressColor}
                        percent={colors?.overduePercent}
                      />
                    ) : null}
                  </Form.Fieldset>
                  <Form.Fieldset cols={1 / 5}>
                    {balance.creditLimit !== 0 ? (
                      <ValueBalanceBar
                        backgroundColor="#e1e1e1"
                        color={colors?.creditColor}
                        percent={colors?.creditPercent}
                        progress="left"
                      />
                    ) : null}
                  </Form.Fieldset>
                </Form.Row>
              </Form.Fieldset>
            ) : null}
          </Form.Row>
          <Form.Row>
            <Form.Fieldset cols={1}>
              {supplier ? (
                <PurchaseTransactionWorkspace
                  embeddedView={true}
                  noEdit={true}
                  additionalVariables={{ excludeSupplier: true }}
                  predefinedFilters={filter}
                  availableColumnsFilter={availableColumnsFilter}
                  availableFiltersFilter={availableFiltersFilter}
                  viewScreenName="/supplier/TransactionEnquiry"
                  contentHeight={
                    showTabs
                      ? getTransactionEnquiryGridHeight('main', embedded)
                      : getTransactionEnquiryGridHeight('main', embedded, 200)
                  }
                  onRowClick={(value: React.SetStateAction<Maybe<IPurchaseTransaction>>) => {
                    setPurchaseTransaction(value);
                    if (stepOverride != null) {
                      setStepOverride(1);
                    }
                    setShowTabs(true);
                  }}
                  ignoreContainerWidth={true}
                />
              ) : null}
            </Form.Fieldset>
          </Form.Row>
          <Form.Row>
            <Form.Fieldset cols={1} minHeight={300}>
              <div id={'pt_tab_container'}>
                {supplier && costViewPermission === CostViewPermission.All && showTabs ? (
                  <Form.TabPanel customAction={<TabButton onClick={() => setShowTabs(false)} text={'Hide Tabs'} />}>
                    <Form.TabContent
                      id="general"
                      label={foreignCurrency ? `General (${balance.currency?.isoCode?.codeAlpha3})` : 'General'}
                    >
                      {renderTransactionDetails(false)}
                    </Form.TabContent>
                    <Form.TabContent id="basecurrency" label="General (GBP)" shouldRender={foreignCurrency}>
                      {renderTransactionDetails(true)}
                    </Form.TabContent>
                    <Form.TabContent id="nominal" label={foreignCurrency ? 'Nominal (GBP)' : 'Nominal'}>
                      <EnquiryNominalTransactions
                        transaction={purchaseTransaction}
                        nominalVat={nominalVat}
                        embedded={embedded}
                        documentType={DocumentType.PurchaseInvoice}
                      />
                    </Form.TabContent>
                    <Form.TabContent id="vat" label={foreignCurrency ? 'VAT (GBP)' : 'VAT'}>
                      <EnquiryVatTransactions
                        transaction={purchaseTransaction}
                        nominalVat={nominalVat}
                        embedded={embedded}
                        documentType={DocumentType.PurchaseInvoice}
                      />
                    </Form.TabContent>
                    <Form.TabContent id="allocation" label="Allocation">
                      <EnquiryAllocationTransactions
                        transaction={purchaseTransaction}
                        allocation={allocation}
                        embedded={embedded}
                        documentType={DocumentType.PurchaseInvoice}
                        columnMeta={ALLOCATION_COLUMN_META}
                      />
                    </Form.TabContent>
                  </Form.TabPanel>
                ) : null}
              </div>
            </Form.Fieldset>
          </Form.Row>
        </Form.Form>
      </Form.FormContainer>
    </CurrencyContext.Provider>
  );
};

export default PurchaseTransactionEnquiryForm;
